import { useRef, useState } from 'react';
import { SignaturePad, type SignaturePadHandle } from '../components/signature-pad';
import { Banner, BigButton, Card, Empty, Label, Screen, TextField } from '../components/ui';
import { saveEcmrLoading } from '../lib/actions';
import { db, useLive } from '../lib/db';
import { countryName } from '../lib/labels';
import { navigate } from '../lib/router';
import { useStop, useStops, useTrip } from '../lib/selectors';
import { useSession } from '../state/session';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ЕЛЕКТРОННА ЧМР — ПОДПИСИТЕ НА ТОВАРЕНЕ.
 *
 *  Две стъпки на един екран, в реда, в който става на рампата:
 *    1. човекът на изпращача проверява данните и се подписва (кл. 22);
 *    2. шофьорът се подписва за превозвача (кл. 23).
 *  Получателят подписва на разтоварване — това е познатият екран „Подпис на
 *  получателя" (кл. 24). Всичко работи и без връзка и се изпраща само.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export function EcmrLoadingScreen({ stopId }: { stopId: string }) {
  const stop = useStop(stopId);
  const trip = useTrip(stop?.tripId ?? null);
  const stops = useStops(stop?.tripId ?? null);
  const driver = useSession((s) => s.driver);
  const senderPad = useRef<SignaturePadHandle>(null);
  const carrierPad = useRef<SignaturePadHandle>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [senderName, setSenderName] = useState('');
  const [carrierName, setCarrierName] = useState(driver?.fullName ?? '');
  const [senderCanvas, setSenderCanvas] = useState<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signed = useLive(
    async () => {
      const rows = await db.events.where('orderStopId').equals(stopId).toArray();
      return rows.some((e) => e.type === 'ecmr_signed');
    },
    [stopId],
    false,
  );

  if (!stop || stop.kind !== 'pickup') {
    return (
      <Screen title="е-ЧМР" back>
        <Empty>Подписите по ЧМР на товарене се правят на адреса за товарене.</Empty>
      </Screen>
    );
  }

  const consignee =
    stops.find((s) => s.kind === 'delivery' && s.sequence > stop.sequence) ??
    [...stops].reverse().find((s) => s.kind === 'delivery') ??
    null;

  function next() {
    const canvas = senderPad.current?.canvas();
    if (senderName.trim().length < 3) {
      setError('Напишете името на човека, който предава стоката.');
      return;
    }
    if (!canvas || senderPad.current?.isEmpty()) {
      setError('Нужен е подписът на изпращача в бялото поле.');
      return;
    }
    /* Копие на платното — следващата стъпка рисува в друго. */
    const copy = document.createElement('canvas');
    copy.width = canvas.width;
    copy.height = canvas.height;
    copy.getContext('2d')?.drawImage(canvas, 0, 0);
    setSenderCanvas(copy);
    setError(null);
    setStep(2);
  }

  async function finish() {
    const canvas = carrierPad.current?.canvas();
    if (!stop || !senderCanvas) return;
    if (carrierName.trim().length < 3) {
      setError('Напишете вашето име и фамилия.');
      return;
    }
    if (!canvas || carrierPad.current?.isEmpty()) {
      setError('Нужен е вашият подпис в бялото поле.');
      return;
    }
    setBusy(true);
    try {
      await saveEcmrLoading({
        tripId: stop.tripId,
        orderStopId: stop.id,
        sender: { name: senderName.trim(), canvas: senderCanvas },
        carrier: { name: carrierName.trim(), canvas },
      });
      navigate('/', true);
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Подписите не можаха да се запишат.');
      setBusy(false);
    }
  }

  return (
    <Screen title="е-ЧМР — товарене" back>
      <div className="space-y-4">
        {signed ? <Banner tone="go">Товарителницата вече е подписана. Нов подпис заменя стария.</Banner> : null}

        <Card className="!bg-paper">
          <Label>Изпращач</Label>
          <p className="text-2xl font-bold leading-tight">{stop.name}</p>
          <p className="text-lg">
            {stop.address}, {stop.city}, {countryName(stop.country)}
          </p>
          {consignee ? (
            <>
              <div className="mt-3">
                <Label>Получател</Label>
              </div>
              <p className="text-xl font-semibold">{consignee.name}</p>
              <p className="text-lg">
                {consignee.city}, {countryName(consignee.country)}
              </p>
            </>
          ) : null}
          <div className="mt-3">
            <Label>Товар</Label>
          </div>
          <p className="text-lg">
            {stop.goodsDescription ?? '—'}
            {stop.weightKg ? ` · ${stop.weightKg} кг` : ''}
            {stop.packagesCount ? ` · ${stop.packagesCount} колета` : ''}
          </p>
          {trip?.vehicle ? <p className="mt-1 text-lg text-muted">Бус {trip.vehicle.plate}</p> : null}
        </Card>

        {step === 1 ? (
          <>
            <p className="text-xl font-semibold">1. Подпис на изпращача</p>
            <p className="text-lg text-muted">Дайте телефона на човека, който предава стоката.</p>
            <TextField label="Име и фамилия на изпращача" value={senderName} onChange={setSenderName} placeholder="Например: Иван Петров" />
            <div>
              <Label>Подпис с пръст</Label>
              <div className="mt-1">
                <SignaturePad ref={senderPad} />
              </div>
              <button
                type="button"
                onClick={() => senderPad.current?.clear()}
                className="tap mt-2 w-full rounded-2xl border-2 border-line px-4 py-3 text-lg font-semibold"
              >
                Изтрий и започни отново
              </button>
            </div>
            {error ? <Banner tone="stop">{error}</Banner> : null}
            <BigButton onClick={next}>Напред — подпис на шофьора</BigButton>
          </>
        ) : (
          <>
            <p className="text-xl font-semibold">2. Вашият подпис (за превозвача)</p>
            <TextField label="Вашето име и фамилия" value={carrierName} onChange={setCarrierName} />
            <div>
              <Label>Подпис с пръст</Label>
              <div className="mt-1">
                <SignaturePad ref={carrierPad} />
              </div>
              <button
                type="button"
                onClick={() => carrierPad.current?.clear()}
                className="tap mt-2 w-full rounded-2xl border-2 border-line px-4 py-3 text-lg font-semibold"
              >
                Изтрий и започни отново
              </button>
            </div>
            {error ? <Banner tone="stop">{error}</Banner> : null}
            <BigButton onClick={() => void finish()} disabled={busy} tone="go">
              {busy ? 'Записвам…' : 'Готово — ЧМР е подписана'}
            </BigButton>
            <button type="button" onClick={() => setStep(1)} className="tap w-full py-2 text-lg text-muted underline">
              Назад към подписа на изпращача
            </button>
          </>
        )}
      </div>
    </Screen>
  );
}
